import { Button, Drawer, Dropdown, Flex, Form, Input, Select, Table, Tag } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import collect from 'collect.js';
import {
  CopyOutlined,
  DownloadOutlined,
  EditOutlined,
  FacebookOutlined,
  GlobalOutlined,
  GoogleOutlined,
  InstagramOutlined,
  MailOutlined,
  MoreOutlined,
  PinterestOutlined,
  PlusOutlined,
  QrcodeOutlined,
  SettingOutlined,
  ShopOutlined,
  TeamOutlined,
  TikTokOutlined,
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import fileDownload from 'js-file-download';
import useApiClient from '../../../hooks/api/useApiClient';
import useForm from '../../../hooks/utility/useForm';
import { TrackingLinkTargetProduct, TrackingLinkType, UtmSource } from '../../../services/exports/Constants';
import useScreenType from '../../../hooks/context/useScreenType';
import useFeedbackLabel from '../../../hooks/ui/useFeedbackLabel';
import str from '../../../services/helpers/Stringable';
import ConfirmCancelModal from '../../../modals/ConfirmCancelModal';
import usePermissions, { ManagerPermission } from '../../../hooks/auth/usePermissions';

export const CustomUtmSource = {
  Other: 'other',
};

function TrackingLinkModal({ selectedTrackingLink, onSuccess, onClose }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:Marketing:TrackingLinks' });

  const { company } = useSelector((state) => state.currentCompany);

  const { TrackingLinksManager } = useApiClient();

  const { isDesktop } = useScreenType();
  const { setErrorFeedback, renderFeedbackLabel } = useFeedbackLabel({ className: 'mb-md' });

  const [loading, setLoading] = useState(false);
  const { data, update } = useForm({
    target_product:
      selectedTrackingLink?.target_product ??
      (company?.storefront_store_id ? TrackingLinkTargetProduct.Storefront : TrackingLinkTargetProduct.Shop),
    target_url:
      selectedTrackingLink?.target_product === TrackingLinkTargetProduct.Custom
        ? selectedTrackingLink.url.split('?')[0]
        : null,
    utm_data: {
      utm_source: selectedTrackingLink?.utm_source
        ? Object.values(UtmSource).includes(selectedTrackingLink.utm_source)
          ? selectedTrackingLink.utm_source
          : CustomUtmSource.Other
        : null,
      utm_source_details: selectedTrackingLink?.utm_source,
      utm_medium: selectedTrackingLink?.utm_medium,
      utm_campaign: selectedTrackingLink?.utm_campaign,
      utm_referrer_id: selectedTrackingLink?.utm_referrer_id,
    },
  });

  const onSave = async () => {
    const body = {
      ...data,
      utm_data: {
        ...data.utm_data,
        utm_source: data.utm_data.utm_source_details ?? data.utm_data.utm_source,
      },
    };

    setLoading(true);
    const { success, data: responseData } = !selectedTrackingLink
      ? await TrackingLinksManager.create(body)
      : await TrackingLinksManager.update(selectedTrackingLink.id, body);
    setLoading(false);

    if (!success) {
      toast.error(t('toasts.failed_to_save_changes'));

      return setErrorFeedback(responseData);
    }

    return onSuccess(responseData);
  };

  const shouldRenderUtmSourceDetails = (utmSource) =>
    [UtmSource.SocialMedia, CustomUtmSource.Other].includes(utmSource);

  const onUtmSourceSelect = (value) => {
    update('utm_data.utm_source', value);

    if (!shouldRenderUtmSourceDetails(value)) {
      update('utm_data.utm_source_details', null);
    }

    const medium =
      {
        [UtmSource.SocialMedia]: 'post',
        [UtmSource.Facebook]: 'post',
        [UtmSource.Instagram]: 'post',
        [UtmSource.Pinterest]: 'post',
        [UtmSource.Tiktok]: 'post',
        [UtmSource.Flyer]: 'qr_code',
        [UtmSource.Email]: 'button',
        [UtmSource.GoogleBusinessProfile]: 'button',
      }[value] ?? null;

    update('utm_data.utm_medium', medium);
  };

  const utmSourceOptions = useMemo(
    () =>
      collect(Object.values(UtmSource))
        .push(CustomUtmSource.Other)
        .map((item) => ({
          label: t(`form.utm_source.options.${item}`, { defaultValue: str(item).capitalize().toString() }),
          value: item,
        }))
        .toArray(),
    [],
  );

  const targetProductOptions = useMemo(
    () =>
      collect(Object.values(TrackingLinkTargetProduct))
        .when(!company.storefront_store_id, (collection) =>
          collection.reject((item) => item === TrackingLinkTargetProduct.Storefront),
        )
        .map((item) => ({
          label: str(item).capitalize().toString(),
          value: item,
        }))
        .toArray(),
    [company.storefront_store_id],
  );

  return (
    <Drawer open size={window.innerWidth * (isDesktop ? 0.4 : 1)} onClose={onClose}>
      <Form layout="vertical">
        {renderFeedbackLabel}
        <Form.Item label={t('form.utm_source.label')} required>
          <Select
            value={data.utm_data.utm_source}
            options={utmSourceOptions}
            onChange={onUtmSourceSelect}
            placeholder={t('form.utm_source.placeholder')}
          />
        </Form.Item>
        {shouldRenderUtmSourceDetails(data.utm_data.utm_source) && (
          <Form.Item label={t('form.utm_source_details.label')} required>
            <Input
              value={data.utm_data.utm_source_details}
              onChange={(e) => update('utm_data.utm_source_details', e.target.value)}
              placeholder={t('form.utm_source_details.placeholder')}
            />
          </Form.Item>
        )}
        <hr className="my-md" />
        <Form.Item label={t('form.target_product.label')} required>
          <Select
            value={data.target_product}
            options={targetProductOptions}
            onChange={(value) => update('target_product', value)}
          />
        </Form.Item>
        {data.target_product === TrackingLinkTargetProduct.Custom && (
          <Form.Item label={t('form.target_url.label')} required>
            <Input
              value={data.target_url}
              onChange={(e) => update('target_url', e.target.value)}
              placeholder={t('form.target_url.placeholder')}
            />
          </Form.Item>
        )}
        <Form.Item label={t('form.utm_medium.label')}>
          <Input
            value={data.utm_data.utm_medium}
            onChange={(e) => update('utm_data.utm_medium', e.target.value)}
            placeholder={t('form.utm_medium.placeholder')}
          />
        </Form.Item>
        <Form.Item label={t('form.utm_campaign.label')}>
          <Input
            value={data.utm_data.utm_campaign}
            onChange={(e) => update('utm_data.utm_campaign', e.target.value)}
            placeholder={t('form.utm_campaign.placeholder')}
          />
        </Form.Item>
        <Flex justify="end">
          <Button type="primary" loading={loading} onClick={onSave}>
            {t(selectedTrackingLink ? 'buttons.update' : 'buttons.create')}
          </Button>
        </Flex>
      </Form>
    </Drawer>
  );
}

export default function TrackingLinksView() {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:Marketing:TrackingLinks' });

  const { TrackingLinksManager } = useApiClient();

  const { can } = usePermissions();

  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);

  const [hasAutomatedLinkSet, setHasAutomatedLinkSet] = useState(true);
  const [showAutomatedLinksCreationModal, setShowAutomatedLinksCreationModal] = useState(false);
  const [creatingAutomatedLinks, setCreatingAutomatedLinks] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedLink, setSelectedLink] = useState(null);

  useEffect(() => {
    loadData();
    TrackingLinksManager.get({
      page: 1,
      filter: {
        type: TrackingLinkType.Auto,
        utm_source: UtmSource.Facebook,
      },
    }).then(({ success, data }) => setHasAutomatedLinkSet(success && data.data.length > 0));
  }, []);

  async function loadData(page = 1, params = {}) {
    setLoading(true);
    const { success, data } = await TrackingLinksManager.get({ page, ...params });
    setLoading(false);

    if (!success) {
      return toast.error(t('toasts.failed_to_load_data'));
    }

    return setData(data);
  }

  async function createAutomatedLinks() {
    setCreatingAutomatedLinks(true);
    const { success } = await TrackingLinksManager.createAutomatedLinks();
    setCreatingAutomatedLinks(false);

    if (!success) {
      return toast.error(t('toasts.failed_to_save_changes'));
    }

    loadData(1)
      .then(() => setShowAutomatedLinksCreationModal(false))
      .then(() => setHasAutomatedLinkSet(true))
      .then(() => toast.success(t('toasts.changes_got_saved')));
  }

  const toggleLinkModal = (link = null) => {
    setSelectedLink(link);
    setShowCreateModal((current) => !current);
  };

  const onTrackingLinkSaved = (data) => {
    setData((current) => ({
      ...current,
      data: collect(current.data)
        .when(
          !!selectedLink,
          (collection) => collection.map((item) => (item.id === data.id ? data : item)),
          (collection) => collection.prepend(data),
        )
        .toArray(),
    }));

    toggleLinkModal();
    toast.success(t('toasts.changes_got_saved'));
  };

  const onLinkCopy = (trackingLink) => {
    navigator.clipboard.writeText(trackingLink.url);

    toast.success(t('toasts.link_got_copied'));
  };

  const onQrCodeDownload = (trackingLink) => {
    fetch(trackingLink.qr_code_url)
      .then((result) => result.blob())
      .then((blob) => fileDownload(blob, `QRCode-${trackingLink.utm_source}.png`, 'image/png'))
      .catch(() => toast.error(t('toasts.failed_to_download_qr_code')));
  };

  return (
    <div>
      <Flex justify="space-between">
        <h2>{t('title')}</h2>
        <Flex gap="middle">
          {can(ManagerPermission.CreateAutomatedTrackingLinks) && !hasAutomatedLinkSet && (
            <Button icon={<SettingOutlined />} onClick={() => setShowAutomatedLinksCreationModal(true)}>
              {t('buttons.create_auto_links')}
            </Button>
          )}
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowCreateModal(true)}>
            {t('buttons.create_link')}
          </Button>
        </Flex>
      </Flex>
      <Table
        dataSource={data.data}
        columns={[
          { width: 100, render: (item) => <img src={item.qr_code_url} alt="" className="h-20 w-20" /> },
          {
            title: t('fields.utm_source'),
            render: (item) => {
              const tagColor =
                {
                  [UtmSource.Flyer]: 'geekblue',
                  [UtmSource.Email]: 'gold',
                  [UtmSource.Google]: 'green',
                  [UtmSource.GoogleBusinessProfile]: 'green',
                  [UtmSource.Facebook]: '#3b5999',
                  [UtmSource.Instagram]: '#F56040',
                  [UtmSource.Pinterest]: '#E60023',
                  [UtmSource.Tiktok]: '#000000',
                  [UtmSource.SocialMedia]: '#55acee',
                }[item.utm_source] ?? 'default';

              const tagIcon =
                {
                  [UtmSource.Flyer]: <QrcodeOutlined />,
                  [UtmSource.Email]: <MailOutlined />,
                  [UtmSource.Google]: <GoogleOutlined />,
                  [UtmSource.GoogleBusinessProfile]: <GoogleOutlined />,
                  [UtmSource.Facebook]: <FacebookOutlined />,
                  [UtmSource.Instagram]: <InstagramOutlined />,
                  [UtmSource.Pinterest]: <PinterestOutlined />,
                  [UtmSource.Tiktok]: <TikTokOutlined />,
                  [UtmSource.SocialMedia]: <TeamOutlined />,
                }[item.utm_source] ?? null;

              return (
                <Tag color={tagColor} icon={tagIcon}>
                  {str(t(`utm_sources.${item.utm_source}`, { defaultValue: item.utm_source }))
                    .capitalize()
                    .toString()}
                </Tag>
              );
            },
          },
          {
            title: t('fields.target_product'),
            render: (item) => {
              const tagColor =
                {
                  [TrackingLinkTargetProduct.Storefront]: '#000000',
                  [TrackingLinkTargetProduct.Shop]: '#69CE8C',
                }[item.target_product] ?? 'default';

              const tagIcon =
                {
                  [TrackingLinkTargetProduct.Storefront]: <GlobalOutlined />,
                  [TrackingLinkTargetProduct.Shop]: <ShopOutlined />,
                }[item.target_product] ?? null;

              return (
                <Tag color={tagColor} icon={tagIcon}>
                  {str(item.target_product).capitalize().toString()}
                </Tag>
              );
            },
          },
          { title: t('fields.type'), dataIndex: 'type' },
          { title: t('fields.utm_medium'), dataIndex: 'utm_medium' },
          { title: t('fields.utm_campaign'), dataIndex: 'utm_campaign' },
          { title: t('fields.utm_referrer_id'), dataIndex: 'utm_referrer_id' },
          {
            width: 25,
            render: (item) => (
              <Dropdown
                menu={{
                  items: [
                    {
                      key: 'copy_link',
                      label: t('buttons.copy_link'),
                      onClick: () => onLinkCopy(item),
                      icon: <CopyOutlined />,
                    },
                    {
                      key: 'download_qr_code',
                      label: t('buttons.download_qr_code'),
                      onClick: () => onQrCodeDownload(item),
                      icon: <DownloadOutlined />,
                    },
                    {
                      type: 'divider',
                    },
                    {
                      key: 'edit',
                      label: t('buttons.edit'),
                      onClick: () => toggleLinkModal(item),
                      icon: <EditOutlined />,
                    },
                  ],
                }}
              >
                <MoreOutlined />
              </Dropdown>
            ),
          },
        ]}
        pagination={{
          current: data.current_page,
          total: data.total,
          pageSize: data.per_page,
          onChange: (page) => loadData(page),
        }}
        loading={loading}
        className="mt-md rounded-none shadow-card bg-white overflow-hidden"
        rowKey={(item) => item.id}
      />
      {showCreateModal && (
        <TrackingLinkModal
          selectedTrackingLink={selectedLink}
          onSuccess={onTrackingLinkSaved}
          onClose={() => toggleLinkModal()}
        />
      )}
      {showAutomatedLinksCreationModal && (
        <ConfirmCancelModal
          show
          title={t('modals.auto_links_creation.title')}
          description={t('modals.auto_links_creation.description')}
          toggleModal={() => setShowAutomatedLinksCreationModal(false)}
          onConfirm={() => createAutomatedLinks()}
          loading={creatingAutomatedLinks}
        />
      )}
    </div>
  );
}
